import React, {FormEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {LANGUAGE_CHOSEN_KEY} from "@view/theme/api/uiSettings";
import {
    Alert, Box, Button, CircularProgress, Divider, IconButton, Link, MenuItem, Select, Stack, TextField, ThemeProvider,
    Tooltip, Typography
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import '@fontsource/unbounded/600.css';
import '@fontsource/unbounded/800.css';
import {buildScenePalette, buildSignInTheme, buildSignInVars, SceneLight, sx} from './sx'
import MenuColorPicker from '@containers/accountStyle/picker/MenuColorPicker';
import {resolveMenuColorHex} from '@containers/accountStyle/helpers/buildAccountStyleTokens';
import {DEFAULT_ACCOUNT_MENU_COLOR} from '@containers/accountStyle/constants/palette';
import {MenuColor} from '@containers/accountStyle/types';
import TrackScene, {DroneKind, SceneMode} from './TrackScene';
import DefenceDisclaimer from './DefenceDisclaimer';
import DefenceReward, {DefenceRewardKind} from './DefenceReward';

import {useTranslation} from "react-i18next";

import {useAuth} from "@containers/auth/hooks/useAuth";

import {useValidationValue} from "@hooks/validate/useValidationValue";
import {getErrorPropsTextField} from '@helpers/error';
import {object} from 'yup';
import {stringReqSchema} from '@helpers/validate/validateSchema';
import {getCookie, setCookie} from "@helpers/cookie/cookie";
import {LOCALES} from "@constants/locales";
import {BASE_TRACK_API_URL} from "@constants/config";

// The legacy pages of track1 read the same cookie, so their languages stay on offer even where
// React itself falls back to Ukrainian. Russian is deliberately left out.
const SIGN_IN_LANGUAGES = [
    {value: 'uk', label: 'Українська'},
    {value: 'en', label: 'English'},
    {value: 'ar', label: 'العربية'},
]

// The page colour, picked from the account menu palette and kept in the browser.
const SIGN_IN_COLOR_KEY = 'signInColor'

const readSignInColor = (): MenuColor => {
    try {
        return window.localStorage.getItem(SIGN_IN_COLOR_KEY) || DEFAULT_ACCOUNT_MENU_COLOR
    } catch (e) {
        return DEFAULT_ACCOUNT_MENU_COLOR
    }
}

// Day or night map, switched in the top bar and kept in the browser; day unless picked otherwise.
const SCENE_LIGHT_KEY = 'signInLight'

const readSceneLight = (): SceneLight => {
    try {
        return window.localStorage.getItem(SCENE_LIGHT_KEY) === 'night' ? 'night' : 'day'
    } catch (e) {
        return 'day'
    }
}

// The air defence scene is an easter egg, switched by the live dot in the caption. It isn't
// remembered: every visit starts calm.
// Presses on the live dot that open the game, each within the pause of the previous one.
const TOGGLE_PRESSES = 5
const TOGGLE_PAUSE_MS = 3000
// Shot down drones that earn a reward; more to come.
const REWARDS: { at: number, kind: DefenceRewardKind }[] = [
    {at: 5, kind: 'coffin'},
    {at: 10, kind: 'kremlin'},
    {at: 15, kind: 'bavovna'},
    {at: 20, kind: 'collapse'},
    {at: 25, kind: 'sunk'},
]

// The new brand name; hidden on the page until it goes public.
const BRAND_NAME = 'TROMVO'
const IS_BRAND_NAME_SHOWN = false

type AutofillField = 'accountCode' | 'login' | 'password'

const SignIng = () => {

    const {t, i18n} = useTranslation()

    const {signIn, signInDemo, isLoading, errorLabel} = useAuth();

    const readoutRef = useRef<HTMLSpanElement>(null)

    const [accountCode, setAccountCode] = useState<string>('')
    const [login, setLogin] = useState<string>('')
    const [password, setPassword] = useState<string>('')
    // Fields the browser filled in itself. Chrome keeps such values from the page until the user
    // touches it, so the field looks empty and its label would sit on top of the text.
    const [autofilled, setAutofilled] = useState<Record<AutofillField, boolean>>(
        {accountCode: false, login: false, password: false})

    const labelProps = (name: AutofillField, value: string) => ({
        InputLabelProps: {shrink: value !== '' || autofilled[name] ? true : undefined},
        inputProps: {
            // MUI marks autofill with these animations; once filled by the browser, keep the label up.
            onAnimationStart: (event: React.AnimationEvent<HTMLInputElement>) => {
                if (event.animationName === 'mui-auto-fill' || event.animationName === 'mui-auto-fill-cancel') {
                    const isFilled = event.animationName === 'mui-auto-fill'
                    setAutofilled((prev) => prev[name] === isFilled ? prev : {...prev, [name]: isFilled})
                }
            },
        },
    })
    const [language, setLanguage] = useState<string>(() => {
        const current = getCookie('language')
        return SIGN_IN_LANGUAGES.some((item) => item.value === current) ? current : 'uk'
    })
    const [isPasswordHintShown, setIsPasswordHintShown] = useState(false)
    const [downed, setDowned] = useState<Record<DroneKind, number>>({shahed: 0, jet: 0, quad: 0})
    const [sceneMode, setSceneMode] = useState<SceneMode>('calm')
    const [color, setColor] = useState<MenuColor>(readSignInColor)
    const accent = resolveMenuColorHex(color)
    const [light, setLight] = useState<SceneLight>(readSceneLight)
    const theme = useMemo(() => buildSignInTheme(accent), [accent])
    const scenePalette = useMemo(() => buildScenePalette(accent, light), [accent, light])

    useEffect(() => {
        const vars = buildSignInVars(accent, light)
        const style = document.documentElement.style
        Object.entries(vars).forEach(([name, value]) => style.setProperty(name, value))
        return () => Object.keys(vars).forEach((name) => style.removeProperty(name))
    }, [accent, light]);

    // By day the fields are grey wells on the coloured card: a filled field keeps its label
    // inside instead of cutting it through the edge.
    const fieldProps = light === 'day'
        ? {variant: 'filled' as const, InputProps: {disableUnderline: true}}
        : {variant: 'outlined' as const}

    const toggleLight = () => {
        const next: SceneLight = light === 'night' ? 'day' : 'night'
        setLight(next)
        try {
            window.localStorage.setItem(SCENE_LIGHT_KEY, next)
        } catch (e) {
            // Private mode: the choice just doesn't survive a reload.
        }
    }

    const changeColor = (next: MenuColor | null) => {
        const value = next ?? DEFAULT_ACCOUNT_MENU_COLOR
        setColor(value)
        try {
            window.localStorage.setItem(SIGN_IN_COLOR_KEY, value)
        } catch (e) {
            // Private mode: the colour just doesn't survive a reload.
        }
    }

    const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false)
    const [reward, setReward] = useState<{ at: number, kind: DefenceRewardKind } | null>(null)
    const rewardedRef = useRef<Set<DefenceRewardKind>>(new Set())
    const pressesRef = useRef({count: 0, at: 0})

    // Switching the game on takes several presses in a row and the disclaimer; one press
    // switches it off.
    const toggleSceneMode = () => {
        if (sceneMode === 'defence') {
            setSceneMode('calm')
            setReward(null)
            return
        }
        const now = Date.now()
        const presses = pressesRef.current
        presses.count = now - presses.at > TOGGLE_PAUSE_MS ? 1 : presses.count + 1
        presses.at = now
        if (presses.count >= TOGGLE_PRESSES) {
            presses.count = 0
            setIsDisclaimerOpen(true)
        }
    }

    const startGame = () => {
        setIsDisclaimerOpen(false)
        setSceneMode('defence')
    }

    // Leaving the tab closes the game, as the press on the live dot would.
    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.hidden) {
                setSceneMode('calm')
                setReward(null)
                setIsDisclaimerOpen(false)
            }
        }
        document.addEventListener('visibilitychange', onVisibilityChange)
        return () => document.removeEventListener('visibilitychange', onVisibilityChange)
    }, []);

    const closeReward = useCallback(() => setReward(null), [])

    const totalDowned = downed.shahed + downed.jet + downed.quad
    useEffect(() => {
        const earned = REWARDS.filter(({at, kind}) => totalDowned >= at && !rewardedRef.current.has(kind)).pop()
        if (earned) {
            REWARDS.filter(({at}) => totalDowned >= at).forEach(({kind}) => rewardedRef.current.add(kind))
            setReward(earned)
        }
    }, [totalDowned]);

    const onDroneDown = useCallback((kind: DroneKind) => {
        setDowned((prev) => ({...prev, [kind]: prev[kind] + 1}))
    }, [])


    const [validateFn, error, errorMessage] = useValidationValue({
        validateSchema: object({
            accountCode: stringReqSchema,
            login: stringReqSchema,
            password: stringReqSchema,
        }),
        value: {
            accountCode, login, password
        },
        dependency: [accountCode, login, password]
    })

    const onLogin = (event?: FormEvent) => {
        event?.preventDefault()
        const validate = validateFn()
        if (!validate) {
            return
        }
        signIn({accountCode, login, password});
    };

    const onLanguageChange = (value: string) => {
        setLanguage(value)
        setCookie('language', value)
        sessionStorage.setItem(LANGUAGE_CHOSEN_KEY, '1')
        i18n.changeLanguage(LOCALES.includes(value) ? value : 'uk')
    }

    return (
        <ThemeProvider theme={theme}>
            <Box sx={sx.root}>
                <Box sx={sx.backdrop}>
                    <Box sx={sx.glows}>
                        <Box sx={sx.glowPlum}/>
                        <Box sx={sx.glowRaspberry}/>
                        <Box sx={sx.glowAmber}/>
                        <Box sx={sx.glowIndigo}/>
                    </Box>
                    <TrackScene mode={sceneMode} palette={scenePalette} readoutRef={readoutRef} onDroneDown={onDroneDown}/>
                    <Box sx={sx.shade}/>
                </Box>

                <Box component={'header'} sx={sx.topBar}>
                    <Box sx={sx.brandMark}>UA-GIS</Box>
                    {sceneMode === 'defence' && totalDowned > 0 && (
                        <Box sx={sx.score} aria-live={'polite'}>
                            <Box component={'span'} sx={sx.scoreLabel}>{t('auth.scoreTitle')}</Box>
                            <span>{t('auth.scoreShaheds')} {downed.shahed}</span>
                            <span>·</span>
                            <span>{t('auth.scoreJets')} {downed.jet}</span>
                            <span>·</span>
                            <span>{t('auth.scoreDrones')} {downed.quad}</span>
                        </Box>
                    )}
                    <Box sx={sx.topBarTools}>
                        <Tooltip title={t(light === 'night' ? 'auth.dayMap' : 'auth.nightMap')}>
                            <IconButton
                                size={'small'}
                                onClick={toggleLight}
                                aria-label={t(light === 'night' ? 'auth.dayMap' : 'auth.nightMap')}
                                sx={sx.lightToggle}
                            >
                                {light === 'night'
                                    ? <LightModeOutlinedIcon fontSize={'small'}/>
                                    : <DarkModeOutlinedIcon fontSize={'small'}/>}
                            </IconButton>
                        </Tooltip>
                        <MenuColorPicker
                            value={color}
                            onSelect={changeColor}
                            onCustomChange={changeColor}
                            tooltip={t('auth.backgroundColor')}
                            iconColor={'var(--sign-in-top-bar-muted, #b9a6bd)'}
                        />
                        <Select
                            variant={'standard'}
                            disableUnderline
                            value={language}
                            onChange={(event) => onLanguageChange(event.target.value)}
                            inputProps={{'aria-label': t('auth.language')}}
                            renderValue={(value) => (
                                <>
                                    <LanguageIcon sx={{fontSize: 18, mr: 1}}/>
                                    {SIGN_IN_LANGUAGES.find((item) => item.value === value)?.label}
                                </>
                            )}
                            sx={sx.language}
                        >
                            {SIGN_IN_LANGUAGES.map((item) => (
                                <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>

                <Box component={'main'} sx={sx.body}>
                    <Box sx={sx.card}>
                        <Typography component={'h1'} sx={sx.title}>{t('auth.formTitle')}</Typography>
                        <Typography sx={sx.subtitle}>{t('auth.formSubtitle')}</Typography>

                        <Stack
                            component={'form'}
                            onSubmit={onLogin}
                            noValidate
                            gap={2.5}
                        >
                            <TextField
                                value={accountCode}
                                onChange={(event) => setAccountCode(event.target.value)}
                                name="account"
                                id={'accountCode'}
                                label={t('auth.login')}
                                autoFocus
                                disabled={isLoading}
                                {...fieldProps}
                                {...labelProps('accountCode', accountCode)}
                                sx={sx.field}
                                {...getErrorPropsTextField(errorMessage, error, 'accountCode')}
                            />

                            <TextField
                                value={login}
                                onChange={(event) => setLogin(event.target.value)}
                                name="login"
                                id={'login'}
                                label={t('auth.accountCode')}
                                autoComplete={'username'}
                                disabled={isLoading}
                                {...fieldProps}
                                {...labelProps('login', login)}
                                sx={sx.field}
                                {...getErrorPropsTextField(errorMessage, error, 'login')}
                            />

                            <TextField
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                type={'password'}
                                name="password"
                                label={t('auth.password')}
                                id={'password'}
                                autoComplete={'current-password'}
                                disabled={isLoading}
                                {...fieldProps}
                                {...labelProps('password', password)}
                                sx={sx.field}
                                {...getErrorPropsTextField(errorMessage, error, 'password')}
                            />

                            <Link
                                component={'button'}
                                type={'button'}
                                onClick={() => setIsPasswordHintShown((prev) => !prev)}
                                aria-expanded={isPasswordHintShown}
                                sx={sx.forgot}
                            >
                                {t('auth.forgotPassword')}
                            </Link>

                            {isPasswordHintShown && (
                                <Typography sx={sx.hint}>{t('auth.forgotPasswordHint')}</Typography>
                            )}

                            {errorLabel && (
                                <Alert severity={'error'} variant={'outlined'}>{t(errorLabel)}</Alert>
                            )}

                            <Button
                                disabled={isLoading}
                                fullWidth
                                type="submit"
                                variant="contained"
                                sx={sx.submit}
                            >
                                {isLoading ? <CircularProgress size={22} color={'inherit'}/> : t('auth.signIn')}
                            </Button>

                            <Divider sx={sx.divider}>{t('auth.or')}</Divider>

                            <Button
                                type={'button'}
                                variant={'outlined'}
                                fullWidth
                                disabled={isLoading}
                                onClick={signInDemo}
                                sx={sx.demo}
                            >
                                {t('auth.demo')}
                            </Button>
                        </Stack>

                        <Box sx={sx.cardFooter}>
                            <span>© {new Date().getFullYear()} UA-GIS</span>
                            {/* The background map is built from OpenStreetMap data (ODbL). */}
                            <a href={'https://www.openstreetmap.org/copyright'} target={'_blank'} rel={'noreferrer'}>
                                © OpenStreetMap
                            </a>
                            <a href={`${BASE_TRACK_API_URL}/api-docs`}>API</a>
                        </Box>
                    </Box>
                </Box>

                <Box sx={sx.caption}>
                    {IS_BRAND_NAME_SHOWN && <Box sx={sx.wordmark}>{BRAND_NAME}</Box>}
                    <Typography sx={sx.taglineTitle}>{t('auth.taglineTitle')}</Typography>
                    <Typography sx={sx.taglineText}>{t('auth.taglineText')}</Typography>
                    <Box sx={sx.readout}>
                        <Box
                            component={'button'}
                            type={'button'}
                            onClick={toggleSceneMode}
                            aria-pressed={sceneMode === 'defence'}
                            aria-label={t(sceneMode === 'calm' ? 'auth.defenceOn' : 'auth.defenceOff')}
                            sx={sceneMode === 'calm' ? sx.liveDot : sx.liveDotDefence}
                        />
                        <Box component={'span'} sx={sx.readoutLabel}>{t('auth.trackPosition')}</Box>
                        <span ref={readoutRef}/>
                    </Box>
                </Box>
            </Box>
            {sceneMode === 'defence' && <DefenceReward kind={reward?.kind ?? null} count={reward?.at ?? 0} onClose={closeReward}/>}
            <DefenceDisclaimer
                open={isDisclaimerOpen}
                onCancel={() => setIsDisclaimerOpen(false)}
                onStart={startGame}
            />
        </ThemeProvider>
    );
};


export default SignIng;
